import {
  AlertCircle,
  ArrowLeft,
  Bell,
  BellOff,
  ChevronRight,
  Download,
  LogOut,
  Monitor,
  Moon,
  Settings2,
  Share,
  Sun,
} from "lucide-react";
import { CEFR_LEVELS, type CefrLevel, type StudyMode } from "@rmnlng/shared";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAdminResetToLevel } from "@/lib/admin";
import { isAdmin, useLogout, useMe, useUpdateMe, type AuthUser } from "@/lib/auth";
import { features } from "@/lib/features";
import { useI18n, useT, type Lang } from "@/lib/i18n";
import { usePWAInstall } from "@/lib/pwa-install";
import {
  detectPushSupport,
  useDisablePush,
  useEnablePush,
  useLocalPushSubscription,
  usePushStatus,
  useSendTestPush,
} from "@/lib/push";
import { useTheme, type Theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

export function SettingsPage() {
  const { data: user } = useMe();
  const logout = useLogout();
  const t = useT();

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-2">
        <Link
          to="/"
          aria-label={t("common.back")}
          className="flex size-9 items-center justify-center rounded-full text-text-2 hover:bg-[color:var(--surface-2)]"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <h1 className="text-[26px] font-bold tracking-[-0.02em]">{t("settings.title")}</h1>
      </div>

      <section>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">
          {t("settings.section_account")}
        </div>
        <div className="overflow-hidden rounded-[var(--r-lg)] border border-border bg-surface">
          <NameRow />
          <div className="border-t border-border px-4 py-3.5">
            <div className="text-[12px] text-text-3">{t("settings.signed_in_as")}</div>
            <div className="mt-0.5 text-[15px] font-medium">{user?.email}</div>
          </div>
        </div>
      </section>

      <AppearanceSection />

      <LanguageSection />

      <StudyModeSection />

      <CurrentLevelSection />

      <StudySection />

      <RemindersSection />

      <InstallSection />

      {/* Admin link — only visible to admins. Previously a bottom-nav
          tab, moved here because the page is import-only and admins are
          a small minority of users. */}
      {isAdmin(user) && (
        <section className="flex flex-col gap-2">
          <div className="mb-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">
            {t("settings.section_admin")}
          </div>
          <Link
            to="/admin"
            className="flex items-center gap-3 rounded-[var(--r-lg)] border border-border bg-surface px-4 py-3.5 transition-colors hover:bg-[color:var(--surface-2)]"
          >
            <div
              className="flex size-9 shrink-0 items-center justify-center rounded-lg"
              style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
            >
              <Settings2 className="size-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-semibold">{t("settings.admin_link_title")}</div>
              <div className="mt-0.5 text-[12px] text-text-3">{t("settings.admin_link_sub")}</div>
            </div>
            <ChevronRight className="size-4 shrink-0 text-text-3" />
          </Link>
          <ResetLevelRow />
        </section>
      )}

      <section>
        <Button variant="secondary" size="block" onClick={logout} className="gap-2">
          <LogOut className="size-4" />
          {t("settings.sign_out")}
        </Button>
      </section>
    </div>
  );
}

function AppearanceSection() {
  const { theme, setTheme } = useTheme();
  const t = useT();
  const options: Array<{
    id: Theme;
    labelKey: "settings.theme_light" | "settings.theme_dark" | "settings.theme_system";
    icon: React.ElementType;
  }> = [
    { id: "light", labelKey: "settings.theme_light", icon: Sun },
    { id: "dark", labelKey: "settings.theme_dark", icon: Moon },
    { id: "system", labelKey: "settings.theme_system", icon: Monitor },
  ];

  return (
    <section>
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">
        {t("settings.section_appearance")}
      </div>
      <div className="rounded-[var(--r-lg)] border border-border bg-surface p-1">
        <div className="grid grid-cols-3 gap-1">
          {options.map(({ id, labelKey, icon: Icon }) => {
            const active = theme === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setTheme(id)}
                className={cn(
                  "flex flex-col items-center justify-center gap-1 rounded-[var(--r-md)] px-3 py-3 text-[13px] font-medium transition-colors",
                  active
                    ? "bg-[color:var(--accent-soft)] text-[color:var(--accent)]"
                    : "text-text-2 hover:bg-[color:var(--surface-2)]",
                )}
                aria-pressed={active}
              >
                <Icon className="size-[18px]" />
                {t(labelKey)}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Language picker ───────────────────────────────────
// Two choices. Clicking the button flips the local i18n context *instantly*
// (so the UI switches before the network roundtrip) and also fires the
// mutation to persist it cross-device. The provider's `useEffect` on
// `me.locale` will quietly reconcile if the server ever rejects the change.
function LanguageSection() {
  const { lang, setLang } = useI18n();
  const t = useT();
  const update = useUpdateMe();

  const options: Array<{ id: Lang; label: string }> = [
    { id: "ru", label: t("settings.language_ru") },
    { id: "ro", label: t("settings.language_ro") },
  ];

  const pickLang = (next: Lang) => {
    if (next === lang) return;
    setLang(next); // optimistic UI flip
    update.mutate({ locale: next });
  };

  return (
    <section>
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">
        {t("settings.section_language")}
      </div>
      <div className="rounded-[var(--r-lg)] border border-border bg-surface p-1">
        <div className="grid grid-cols-2 gap-1">
          {options.map(({ id, label }) => {
            const active = lang === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => pickLang(id)}
                className={cn(
                  "rounded-[var(--r-md)] px-3 py-3 text-[13px] font-medium transition-colors",
                  active
                    ? "bg-[color:var(--accent-soft)] text-[color:var(--accent)]"
                    : "text-text-2 hover:bg-[color:var(--surface-2)]",
                )}
                aria-pressed={active}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Study mode picker ──────────────────────────────────
// Two stops: Open (free study, all levels mixed) vs. Level (guided
// path scoped to `currentLevel`). Used to live in the Home header as
// Name editor — lives inside the Account card, above the email row.
// Saves on blur OR Enter. Empty value clears the name (column → null);
// the Home greeting then falls back to the email prefix. Optimistic via
// `useUpdateMe.variables` so the user sees their typed value reflected
// instantly even before the PATCH round-trips.
function NameRow() {
  const { data: user } = useMe();
  const update = useUpdateMe();
  const t = useT();
  // Local draft state — initialized from `user.name`, kept in sync via
  // an effect that fires only when the server-side value actually
  // changes (avoid clobbering an in-progress edit).
  const initial = user?.name ?? "";
  const [draft, setDraft] = useState(initial);
  const lastInitialRef = useRef(initial);
  if (lastInitialRef.current !== initial) {
    // Server-side value changed (refetch / login / mutation success on
    // a different field). Resync the draft only when it matches the
    // previous server value — preserves an in-progress edit otherwise.
    if (draft === lastInitialRef.current) setDraft(initial);
    lastInitialRef.current = initial;
  }

  // Reads the input value off the event target rather than from the
  // `draft` closure to avoid stale-state when React batches the
  // change+blur sequence (synthetic-event tests; can also happen in
  // real life if the user blurs before React's microtask flushes).
  const commit = (raw: string) => {
    const trimmed = raw.trim();
    const current = (user?.name ?? "").trim();
    if (trimmed === current) return;
    // Empty string explicitly clears — the server maps "" → null.
    update.mutate({ name: trimmed });
  };

  return (
    <div className="px-4 py-3.5">
      <label htmlFor="settings-name" className="text-[12px] text-text-3">
        {t("settings.name_label")}
      </label>
      <input
        id="settings-name"
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          }
        }}
        placeholder={t("settings.name_placeholder")}
        className="mt-0.5 w-full bg-transparent text-[15px] font-medium text-text outline-none placeholder:text-text-3"
      />
    </div>
  );
}

// `<ModeSwitch>` but the header was crowded — moved here so the mode
// hint card on Home can link to one stable destination. Switching is
// lossless: only changes what the queue serves; SRS state, streak,
// and totals are shared across both modes.
function StudyModeSection() {
  const { data: user } = useMe();
  const update = useUpdateMe();
  const t = useT();

  // Optimistic — reflect the click instantly without waiting for the
  // PATCH round-trip. Mutation invalidates the queue cache on success
  // so Home re-fetches with the new scope.
  const current: StudyMode = update.variables?.studyMode ?? user?.studyMode ?? "LEVEL";
  const currentLevel = user?.currentLevel ?? "A0";

  function pick(next: StudyMode) {
    if (next === current) return;
    update.mutate({ studyMode: next });
  }

  // Sub-label for LEVEL shows the actual level (e.g. "A1") to answer
  // "what level am I scoped to?" without an extra trip to the Levels
  // page. Mirrors the original `<ModeSwitch>` design.
  const allStops: Array<{ id: StudyMode; label: string; sub: string }> = [
    { id: "OPEN", label: t("settings.mode_open_label"), sub: t("settings.mode_open_sub") },
    { id: "LEVEL", label: t("settings.mode_level_label"), sub: t("settings.mode_level_sub", { level: currentLevel }) },
    {
      id: "SCENARIOS",
      label: t("settings.mode_scenarios_label"),
      sub: t("settings.mode_scenarios_sub"),
    },
  ];
  const stops = allStops.filter((s) => features.skillsEnabled || s.id !== "SCENARIOS");

  return (
    <section>
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">
        {t("settings.section_study_mode")}
      </div>
      <div className="overflow-hidden rounded-[var(--r-lg)] border border-border bg-surface">
        <div className="px-4 py-3.5">
          <div className="text-[15px] font-semibold">{t("settings.study_mode_title")}</div>
          <div className="mt-0.5 text-[12px] text-text-3">{t("settings.study_mode_hint")}</div>
        </div>
        <div className="border-t border-border p-1">
          <div className={cn("grid gap-1", stops.length === 3 ? "grid-cols-3" : "grid-cols-2")}>
            {stops.map((s) => {
              const active = current === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => pick(s.id)}
                  disabled={update.isPending && update.variables?.studyMode === s.id}
                  aria-pressed={active}
                  className={cn(
                    "flex flex-col items-center justify-center gap-0.5 rounded-[var(--r-md)] px-3 py-2.5 transition-colors",
                    active
                      ? "bg-[color:var(--accent-soft)] text-[color:var(--accent)]"
                      : "text-text-2 hover:bg-[color:var(--surface-2)]",
                  )}
                >
                  <span className="text-[13px] font-medium">{s.label}</span>
                  <span className="text-[11px] text-text-3">{s.sub}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// Manual escape hatch for setting `currentLevel`. Originally the field
// was server-managed only (advanced via the readiness exam); we open it
// here for two legitimate use cases:
//
//   1. New user who already studied Romanian elsewhere wants to skip
//      ahead to A2/B1 instead of grinding A0+A1 from scratch.
//   2. Existing user who advanced to A2 wants to revisit A1 vocabulary
//      / grammar without losing their current-level state permanently.
//
// Because picking forward bypasses the exam gate, we use a `confirm()`
// dialog with explicit copy. Going back to a lower level is also free
// — no lost progress (SRS state is unchanged regardless of the level
// scope used to surface cards).
function CurrentLevelSection() {
  const { data: user } = useMe();
  const update = useUpdateMe();
  const t = useT();

  const current: CefrLevel = update.variables?.currentLevel ?? user?.currentLevel ?? "A0";

  function pick(next: CefrLevel) {
    if (next === current) return;
    // Browser confirm() is enough for an admin-y settings action — no
    // need to wire a custom modal. Copy makes the trade-off explicit.
    const ok = window.confirm(
      t("settings.level_confirm", {
        current,
        next,
      }),
    );
    if (!ok) return;
    update.mutate({ currentLevel: next });
  }

  return (
    <section>
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">
        {t("settings.section_current_level")}
      </div>
      <div className="overflow-hidden rounded-[var(--r-lg)] border border-border bg-surface">
        <div className="px-4 py-3.5">
          <div className="text-[15px] font-semibold">{t("settings.current_level_title")}</div>
          <div className="mt-0.5 text-[12px] text-text-3">{t("settings.current_level_hint")}</div>
        </div>
        <div className="border-t border-border p-1">
          {/* 7-up grid (A0–C2). Tight on mobile, but at 7 narrow buttons
              it stays scannable — same density as the level strip on
              the Progress page. */}
          <div className="grid grid-cols-7 gap-1">
            {CEFR_LEVELS.map((level) => {
              const active = current === level;
              return (
                <button
                  key={level}
                  type="button"
                  onClick={() => pick(level)}
                  disabled={update.isPending && update.variables?.currentLevel === level}
                  aria-pressed={active}
                  className={cn(
                    "flex flex-col items-center justify-center gap-0 rounded-[var(--r-md)] px-1 py-2 transition-colors",
                    active
                      ? "bg-[color:var(--accent-soft)] text-[color:var(--accent)]"
                      : "text-text-2 hover:bg-[color:var(--surface-2)]",
                  )}
                >
                  <span className="font-mono text-[12px] font-semibold">{level}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// Three independent per-day caps. `dailyBatchSize` is vocab (legacy name kept
// for schema/API compat — UI label says "new words"); the other two were
// added so a user wouldn't drown in 10 new grammar rules + 10 new quizzes
// per day on top of vocab. 0 is allowed on the grammar/quiz rows to fully
// pause introduction without zeroing vocab.
const VOCAB_BATCH_OPTIONS = [5, 10, 15, 20, 30] as const;
const GRAMMAR_BATCH_OPTIONS = [0, 1, 2, 3, 5] as const;
const QUIZ_BATCH_OPTIONS = [0, 3, 5, 10, 15] as const;

type BatchField = "dailyBatchSize" | "newGrammarPerDay" | "newQuizPerDay";

function StudySection() {
  const { data: user } = useMe();
  const update = useUpdateMe();
  const t = useT();

  return (
    <section>
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">
        {t("settings.section_study")}
      </div>
      <div className="overflow-hidden rounded-[var(--r-lg)] border border-border bg-surface">
        <BatchPickerRow
          field="dailyBatchSize"
          options={VOCAB_BATCH_OPTIONS}
          fallback={10}
          titleKey="settings.daily_batch_title"
          hintKey="settings.daily_batch_hint"
          user={user}
          update={update}
        />
        <div className="border-t border-border" />
        <BatchPickerRow
          field="newGrammarPerDay"
          options={GRAMMAR_BATCH_OPTIONS}
          fallback={1}
          titleKey="settings.new_grammar_title"
          hintKey="settings.new_grammar_hint"
          user={user}
          update={update}
        />
        <div className="border-t border-border" />
        <BatchPickerRow
          field="newQuizPerDay"
          options={QUIZ_BATCH_OPTIONS}
          fallback={3}
          titleKey="settings.new_quiz_title"
          hintKey="settings.new_quiz_hint"
          user={user}
          update={update}
        />
        {update.error && (
          <div className="border-t border-border px-4 py-2.5 text-[12px] text-[color:var(--again)]" role="alert">
            {update.error instanceof Error ? update.error.message : t("settings.save_failed")}
          </div>
        )}
      </div>
    </section>
  );
}

function BatchPickerRow({
  field,
  options,
  fallback,
  titleKey,
  hintKey,
  user,
  update,
}: {
  field: BatchField;
  options: ReadonlyArray<number>;
  fallback: number;
  titleKey: "settings.daily_batch_title" | "settings.new_grammar_title" | "settings.new_quiz_title";
  hintKey: "settings.daily_batch_hint" | "settings.new_grammar_hint" | "settings.new_quiz_hint";
  user: AuthUser | undefined;
  update: ReturnType<typeof useUpdateMe>;
}) {
  const t = useT();
  // Optimistic selection — read from the in-flight mutation variables for
  // *this specific field* so two pickers in flight don't bleed into each
  // other (only one fires at a time today, but cheap correctness).
  const optimistic = update.variables?.[field];
  const current = optimistic ?? user?.[field] ?? fallback;
  return (
    <>
      <div className="px-4 py-3.5">
        <div className="text-[15px] font-semibold">{t(titleKey)}</div>
        <div className="mt-0.5 text-[12px] text-text-3">{t(hintKey)}</div>
      </div>
      <div className="border-t border-border p-1">
        <div className="grid grid-cols-5 gap-1">
          {options.map((n) => {
            const active = current === n;
            return (
              <button
                key={n}
                type="button"
                disabled={update.isPending && optimistic === n}
                onClick={() => {
                  if (n !== current) update.mutate({ [field]: n });
                }}
                className={cn(
                  "rounded-[var(--r-md)] px-3 py-2.5 text-[13px] font-medium tabular-nums transition-colors",
                  active
                    ? "bg-[color:var(--accent-soft)] text-[color:var(--accent)]"
                    : "text-text-2 hover:bg-[color:var(--surface-2)]",
                )}
                aria-pressed={active}
              >
                {n}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

function RemindersSection() {
  const support = detectPushSupport();
  const { data: user } = useMe();
  const { data: status } = usePushStatus();
  const { data: localSub } = useLocalPushSubscription();
  const enable = useEnablePush();
  const disable = useDisablePush();
  const test = useSendTestPush();
  const t = useT();

  const enabledOnThisDevice = !!localSub;
  const busy = enable.isPending || disable.isPending;
  const error = enable.error ?? disable.error ?? test.error ?? null;

  return (
    <section>
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">
        {t("settings.section_reminders")}
      </div>
      <div className="overflow-hidden rounded-[var(--r-lg)] border border-border bg-surface">
        <div className="flex items-start gap-3 px-4 py-4">
          <div
            className="flex size-10 shrink-0 items-center justify-center rounded-full"
            style={{
              background: enabledOnThisDevice ? "var(--accent-soft)" : "var(--surface-2)",
              color: enabledOnThisDevice ? "var(--accent)" : "var(--text-3)",
            }}
          >
            {enabledOnThisDevice ? <Bell className="size-4" /> : <BellOff className="size-4" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-semibold">{t("settings.push_title")}</div>
            <div className="mt-0.5 text-[12px] text-text-3">
              <StatusLine
                support={support}
                configured={status?.configured ?? false}
                enabledHere={enabledOnThisDevice}
                totalSubs={status?.subscriptions ?? 0}
              />
            </div>
          </div>
        </div>

        <div className="border-t border-border px-4 py-3">
          {support !== "supported" ? (
            <UnsupportedNote state={support} />
          ) : !status?.configured ? (
            <div className="flex items-center gap-2 text-[12px] text-text-3">
              <AlertCircle className="size-3.5" />
              {t("settings.push_server_unconfigured")} <code className="font-mono">bun run vapid:keys</code>.
            </div>
          ) : enabledOnThisDevice ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="secondary" size="sm" disabled={busy} onClick={() => disable.mutate()}>
                {disable.isPending ? t("settings.push_disabling") : t("settings.push_disable")}
              </Button>
              {/* Diagnostic: immediate push bypassing the reminder cron. Lets
                  the user verify the pipeline is working when the daily
                  reminder fails to show. `ghost` so it doesn't compete with
                  the primary disable action. */}
              {isAdmin(user) && (
                <Button variant="ghost" size="sm" disabled={test.isPending} onClick={() => test.mutate()}>
                  {test.isPending ? t("settings.push_sending_test") : t("settings.push_send_test")}
                </Button>
              )}
            </div>
          ) : (
            <Button size="sm" disabled={busy} onClick={() => enable.mutate()}>
              {enable.isPending ? t("settings.push_enabling") : t("settings.push_enable")}
            </Button>
          )}

          {/* Test-push result — shown only after a successful send so the
              user can eyeball "attempted" vs "delivered". If attempted=0
              there were no subs on the server (something's very wrong).
              If delivered=0 but attempted>0, subs exist but something
              (likely VAPID mismatch) is rejecting the send. */}
          {isAdmin(user) && test.data && (
            <p className="mt-2 text-[12px] text-text-3" role="status">
              {test.data.attempted === 0
                ? t("settings.push_test_none_attempted")
                : test.data.delivered === 0
                  ? t("settings.push_test_none_delivered", { attempted: test.data.attempted })
                  : t("settings.push_test_result", {
                      delivered: test.data.delivered,
                      attempted: test.data.attempted,
                    })}
            </p>
          )}

          {error && (
            <p className="mt-2 text-[12px] text-[color:var(--again)]" role="alert">
              {error instanceof Error ? error.message : String(error)}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

function StatusLine({
  support,
  configured,
  enabledHere,
  totalSubs,
}: {
  support: ReturnType<typeof detectPushSupport>;
  configured: boolean;
  enabledHere: boolean;
  totalSubs: number;
}) {
  const t = useT();
  if (support === "insecure-context") return <span>{t("settings.push_status_https")}</span>;
  if (support === "unsupported") return <span>{t("settings.push_status_unsupported")}</span>;
  if (!configured) return <span>{t("settings.push_status_unconfigured")}</span>;
  if (enabledHere) {
    const other = Math.max(0, totalSubs - 1);
    return (
      <span>
        {t("settings.push_status_enabled_here")}
        {other > 0 && t("settings.push_status_plus_others", { n: other })}
      </span>
    );
  }
  if (totalSubs > 0) {
    // Singular/plural split — keeping it simple (2 forms) since this string
    // only ever takes small integer counts.
    const key = totalSubs === 1 ? "settings.push_status_only_others" : "settings.push_status_only_others_plural";
    return <span>{t(key, { n: totalSubs })}</span>;
  }
  return <span>{t("settings.push_status_not_enabled")}</span>;
}

function UnsupportedNote({ state }: { state: ReturnType<typeof detectPushSupport> }) {
  const t = useT();
  return (
    <div className="flex items-center gap-2 text-[12px] text-text-3">
      <AlertCircle className="size-3.5" />
      {state === "insecure-context" ? t("settings.push_requires_https") : t("settings.push_unavailable")}
    </div>
  );
}

// ─── Install as a PWA ───────────────────────────────────────────────
//
// Escape-hatch row paired with the floating <PWAInstallPrompt>. The
// banner respects an incremental dismissal cooldown; this row ignores
// it — if the user goes looking, they're explicitly opting in.
//
// Auto-hides when the app is already installed or the platform doesn't
// support installable PWAs. On Chromium it requires the
// `beforeinstallprompt` event to have been captured (otherwise we have
// no API to call); on iOS Safari it shows always-visible Share→Add
// instructions since there's no programmatic install.

function InstallSection() {
  const t = useT();
  const { platform, shouldShowSettingsRow, install } = usePWAInstall();

  if (!shouldShowSettingsRow) return null;

  const isIOS = platform === "ios-safari";

  return (
    <section>
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">
        {t("settings.section_install")}
      </div>
      <div className="overflow-hidden rounded-[var(--r-lg)] border border-border bg-surface">
        {isIOS ? (
          // iOS: no programmatic install → static instruction card.
          // Always-visible (rather than collapsed) since there's nothing
          // for the user to click and the steps are short.
          <div className="flex items-start gap-3 px-4 py-4">
            <div
              className="flex size-10 shrink-0 items-center justify-center rounded-full"
              style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
            >
              <Download className="size-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-semibold">{t("pwa.install_ios_title")}</div>
              <div className="mt-1 flex items-center gap-1 text-[13px] text-text-2">
                {t("pwa.install_ios_hint_before")}
                <Share className="inline size-4" aria-hidden="true" />
                {t("pwa.install_ios_hint_after")}
              </div>
            </div>
          </div>
        ) : (
          // Chromium: native install button. Calls the captured
          // `beforeinstallprompt` event on tap.
          <button
            type="button"
            onClick={install}
            className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-[color:var(--surface-2)]"
          >
            <div
              className="flex size-9 shrink-0 items-center justify-center rounded-lg"
              style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
            >
              <Download className="size-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-semibold">{t("pwa.install_title")}</div>
              <div className="mt-0.5 text-[12px] text-text-3">{t("pwa.install_hint")}</div>
            </div>
            <ChevronRight className="size-4 shrink-0 text-text-3" />
          </button>
        )}
      </div>
    </section>
  );
}

// ─── Admin debug: reset to a specific level ─────────────────────────
//
// Row of CEFR-level buttons that POST `/admin/reset-to-level/:level`.
// Sets `currentLevel` and wipes all LevelUpAttempt rows so the admin
// can take an exam, reset, take it again — without waiting for the
// 24h cooldown. Doesn't touch SRS state (Word/Grammar/Quiz progress
// stays put), so make sure your readiness gate still holds for the
// level you reset to.
//
// Admin-gated server-side; rendered only inside the `{isAdmin(user)}`
// section of the Settings page. Belt-and-suspenders.
function ResetLevelRow() {
  const reset = useAdminResetToLevel();
  const t = useT();
  const me = useMe();
  const currentLevel = me.data?.currentLevel ?? "A0";

  function onClick(level: CefrLevel) {
    if (reset.isPending) return;
    // Hitting the endpoint when level === currentLevel is intentional:
    // the level update is a no-op but the attempts get wiped, which is
    // exactly the cooldown-clearing case (admin retook the exam, want
    // another shot without waiting 24 h).
    if (!confirm(t("settings.reset_level_confirm", { level }))) return;
    reset.mutate(level);
  }

  return (
    <div className="overflow-hidden rounded-[var(--r-lg)] border border-border bg-surface">
      <div className="px-4 py-3.5">
        <div className="text-[15px] font-semibold">{t("settings.reset_level_title")}</div>
        <div className="mt-0.5 text-[12px] text-text-3">{t("settings.reset_level_hint")}</div>
      </div>
      <div className="border-t border-border p-2">
        <div className="grid grid-cols-4 gap-1.5">
          {CEFR_LEVELS.map((level) => {
            const isCurrent = level === currentLevel;
            return (
              <button
                key={level}
                type="button"
                onClick={() => onClick(level)}
                disabled={reset.isPending}
                className={cn(
                  "rounded-[var(--r-sm)] px-2 py-2 font-mono text-[12px] font-bold transition-colors disabled:opacity-50",
                  isCurrent
                    ? "bg-[color:var(--accent-soft)] text-[color:var(--accent)]"
                    : "text-text-2 hover:bg-[color:var(--surface-2)]",
                )}
              >
                {level}
              </button>
            );
          })}
        </div>
        {reset.data && (
          <div className="mt-2 px-1 font-mono text-[11px] text-text-3 tabular-nums">
            {t("settings.reset_level_done", {
              level: reset.data.currentLevel,
              n: reset.data.deletedAttempts,
            })}
          </div>
        )}
      </div>
    </div>
  );
}
